import * as pdfjsLib from 'pdfjs-dist';
import {
  PDFDocumentProxy,
  PDFPageProxy,
  RenderTask
} from 'pdfjs-dist/types/src/pdf';

import { IEnvelopeFilePreview, IPreviewData } from '@store/modules/envelopes/types';
import { getPdfHeaders, PDF_DOCUMENT_OPTIONS, setupPdfWorker } from '@views/envelopes/components/EnvelopeCard/pdfUtils';

const PDF_RENDER_SCALE = 2;
const PDF_RENDER_MIME_TYPE = 'image/png';
const pdfPreviewCache = new Map<string, Promise<IEnvelopeFilePreview>>();

setupPdfWorker();

const getImageCodeFromDataUrl = (dataUrl: string): string => (
  dataUrl.replace(/^data:image\/png;base64,/, '')
);

const renderPdfPage = async (page: PDFPageProxy): Promise<IPreviewData> => {
  const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
  const canvas = window.document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas context is unavailable');
  }

  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);

  const renderTask: RenderTask = page.render({
    canvasContext: context,
    viewport
  });

  await renderTask.promise;

  return {
    dimensions: {
      height: canvas.height,
      width: canvas.width
    },
    fileCode: getImageCodeFromDataUrl(canvas.toDataURL(PDF_RENDER_MIME_TYPE)),
    mimeType: PDF_RENDER_MIME_TYPE,
    orientation: canvas.width > canvas.height ? 'LANDSCAPE' : 'PORTRAIT'
  };
};

const clonePdfPreview = (
  preview: IEnvelopeFilePreview,
  fileName: string
): IEnvelopeFilePreview => ({
  ...preview,
  data: [...preview.data],
  fileName
});

const loadPdfPreview = async (
  url: string,
  fileName: string
): Promise<IEnvelopeFilePreview> => {
  let pdfDocument: PDFDocumentProxy | null = null;
  let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null;

  try {
    loadingTask = pdfjsLib.getDocument({
      ...PDF_DOCUMENT_OPTIONS,
      disableAutoFetch: true,
      disableRange: false,
      disableStream: true,
      httpHeaders: getPdfHeaders(),
      rangeChunkSize: 65536,
      url
    });
    pdfDocument = await loadingTask.promise;

    const data: IPreviewData[] = [];

    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
      const page = await pdfDocument.getPage(pageNumber);
      data.push(await renderPdfPage(page));
    }

    return {
      data,
      fileName,
      isAvailable: Boolean(data.length),
      total: pdfDocument.numPages
    };
  } finally {
    await pdfDocument?.destroy();
    loadingTask?.destroy();
  }
};

export const renderPdfPreview = async (
  url: string,
  fileName: string
): Promise<IEnvelopeFilePreview> => {
  const cachedPreview = pdfPreviewCache.get(url);

  if (cachedPreview) {
    return cachedPreview.then((preview) => clonePdfPreview(preview, fileName));
  }

  const previewPromise = loadPdfPreview(url, fileName)
    .catch((err) => {
      pdfPreviewCache.delete(url);
      throw err;
    });

  pdfPreviewCache.set(url, previewPromise);

  return previewPromise.then((preview) => clonePdfPreview(preview, fileName));
};
